import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { concatMap, map } from 'rxjs/operators';

import { QueryData } from '../../../shared/models/payloads/query-data.payload';
import { environment } from '../../../../environments/environment';
import { ExamResult, ListExamsResult } from '../../../shared/models/api-results/exam.api-result';
import { prepareQueryParams } from '../../../shared/utils/api-transformations';
import {
  AddExamPayload,
  ExamsLandingQueryData,
  ExamsQueryData,
  ExamStudentsRankingQueryData,
  UpdateExamPayload,
  UpdateExamPublishAtPayload,
  WExamTypeaheadQueryData,
  WExamsQueryData,
} from '../../../shared/models/payloads/exam.payload';
import { AddedEntity } from '../../../shared/models/api-results/base.api-result';
import { ExamPublicationStatus } from '../../../shared/models/enums/publication-status.enums';
import { ListUniversitiesResult } from '../../../shared/models/api-results/university.api-result';
import { ListExamSessionsResult } from '../../../shared/models/api-results/exam-session.api-result';
import { ListSpecialtiesResult } from '../../../shared/models/api-results/specialty.api-result';
import { AppTypeService } from '../app-type.service';

@Injectable({
  providedIn: 'root',
})
export class HttpExamService {
  constructor(private http: HttpClient, private appTypeService: AppTypeService) {}

  list(params: ExamsQueryData) {
    return this.http
      .get(`${environment.api_base_url}/exams/list_published_exams`, {
        params: prepareQueryParams(params),
      })
      .pipe(map((res) => res as ListExamsResult));
  }

  listLanding(params: ExamsLandingQueryData) {
    return this.http
      .get(`${environment.api_base_url}/exams/list_exams_published_on_landing`, {
        params: prepareQueryParams(params),
      })
      .pipe(map((res) => res as ListExamsResult));
  }

  get(id: string) {
    return this.http
      .get(`${environment.api_base_url}/exams/${id}/exam_detail_for_student`)
      .pipe(map((res) => res as ExamResult));
  }

  listOtherSpecialties(id: string) {
    return this.http
      .get(`${environment.api_base_url}/specialties/list_other_specialties_for_exam/${id}`)
      .pipe(map((res) => res as ListSpecialtiesResult));
  }

  listStudentsRanking(id: string, params: ExamStudentsRankingQueryData) {
    return this.http
      .get(`${environment.api_base_url}/exam_sessions/${id}/list_student_rankings`, {
        params: prepareQueryParams(params),
      })
      .pipe(map((res) => res as ListExamSessionsResult));
  }

  listUniversitiesRanking(id: string, params: QueryData) {
    return this.http
      .get(`${environment.api_base_url}/exams/${id}/list_university_rankings_of_an_exam`, {
        params: prepareQueryParams(params),
      })
      .pipe(map((res) => res as ListUniversitiesResult));
  }

  wGet(id: string) {
    return this.http
      .get(`${environment.api_base_url}/exams/${id}/exam_detail`)
      .pipe(map((res) => res as ExamResult));
  }

  wWriterList(params: WExamsQueryData) {
    return this.appTypeService.currentPassWriterUniversityAndSemester$.pipe(
      concatMap(([currentPassWriterUniversity, currentPassWriterSemester]) => {
        params['university.id'] = currentPassWriterSemester
          ? undefined
          : currentPassWriterUniversity?.id;
        params['semester.id'] = currentPassWriterSemester?.id;
        return this.http
          .get(`${environment.api_base_url}/exams/me/list_my_exams`, {
            params: prepareQueryParams(params),
          })
          .pipe(map((res) => res as ListExamsResult));
      })
    );
  }

  wChiefWriterList(params: WExamsQueryData) {
    return this.appTypeService.currentPassWriterUniversityAndSemester$.pipe(
      concatMap(([currentPassWriterUniversity, currentPassWriterSemester]) => {
        params['university.id'] = currentPassWriterSemester
          ? undefined
          : currentPassWriterUniversity?.id;
        params['semester.id'] = currentPassWriterSemester?.id;
        return this.http
          .get(`${environment.api_base_url}/exams/list_exams`, {
            params: prepareQueryParams(params),
          })
          .pipe(map((res) => res as ListExamsResult));
      })
    );
  }

  wWriterTypeaheadList(params: WExamTypeaheadQueryData) {
    return this.http
      .get(`${environment.api_base_url}/exams/me/list_my_simplified_exams`, {
        params: prepareQueryParams(params),
      })
      .pipe(map((res) => res as ListExamsResult));
  }

  wChiefWriterTypeaheadList(params: WExamTypeaheadQueryData) {
    return this.http
      .get(`${environment.api_base_url}/exams/list_simplified_exams`, {
        params: prepareQueryParams(params),
      })
      .pipe(map((res) => res as ListExamsResult));
  }

  wAdd(payload: AddExamPayload) {
    return this.http
      .post(`${environment.api_base_url}/exams/add_exam`, payload)
      .pipe(map((res) => res as AddedEntity));
  }

  wDelete(id: string) {
    return this.http.post(`${environment.api_base_url}/exams/${id}/soft_delete_exam`, {});
  }

  wUpdate(id: string, payload: UpdateExamPayload) {
    return this.http.post(`${environment.api_base_url}/exams/${id}/update_exam`, payload);
  }

  wUpdatePublicationStatus(id: string, publicationStatus: ExamPublicationStatus) {
    return this.http.post(
      `${environment.api_base_url}/exams/${id}/update_exam_publication_status`,
      {
        publicationStatus,
      }
    );
  }

  wUpdatePublishAt(id: string, payload: UpdateExamPublishAtPayload) {
    return this.http.post(`${environment.api_base_url}/exams/${id}/reschedule_exam`, payload);
  }
}
